/**
 * CallManager - Modal for managing call history and direct dialing
 * 
 * Features:
 * - View all call history with pagination
 * - Basic filters (status, direction)
 * - Search by phone number
 * - Direct dial to any number
 * - Click to call back
 */

import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import "./CallManager.css";
import {
    IoIosRefresh,
    IoMdCall,
    IoMdArrowRoundBack,
    IoMdArrowRoundForward,
    IoMdClose,
} from "react-icons/io";
import {
    MdCall,
    MdCallEnd,
    MdCallMade,
    MdCallReceived,
    MdCallMissed,
    MdPhone,
    MdDialpad,
    MdHistory,
    MdSearch,
    MdFilterList,
    MdPlayArrow,
    MdPause,
    MdHeadset,
} from "react-icons/md";
import { CircularProgress, Tooltip, IconButton, TextField } from "@mui/material";
import toast from "react-hot-toast";
import axios from "axios";
import { CallContext } from "../CallContext";

const API_BASE = process.env.REACT_APP_API_URL || "https://casualfootwears.com/api";

// Call list item component
const CallListItem = ({ call, theme, onCallBack, onViewDetails, onPlayRecording, isPlaying, playingCallId }) => {
    const isInbound = call.direction === 'inbound';
    const isMissed = call.is_missed;
    const isCompleted = call.status === 'ended' && call.answered_at;
    const isFailed = call.status === 'failed';
    const hasRecording = call.has_recording;
    const isCurrentlyPlaying = playingCallId === call.call_id && isPlaying;

    // Phone number to display (the other party)
    const displayPhone = isInbound ? call.caller_phone : call.recipient_phone;

    // Format phone nicely
    const formatPhone = (phone) => {
        if (!phone) return 'Unknown';
        if (phone.length > 10) {
            return `+${phone.slice(0, 2)} ${phone.slice(2)}`;
        }
        return phone;
    };

    // Format date/time in IST
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);

        // Format options for IST display
        const timeOptions = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata'
        };
        const dateOptions = {
            day: 'numeric',
            month: 'short',
            timeZone: 'Asia/Kolkata'
        };

        const timeStr = date.toLocaleTimeString('en-IN', timeOptions);
        const dateStr2 = date.toLocaleDateString('en-IN', dateOptions);

        // Return combined: "2 Feb 11:57 PM"
        return `${dateStr2}, ${timeStr}`;
    };

    // Status info
    const getStatusInfo = () => {
        if (isMissed) {
            return { text: 'Missed', color: '#f44336', icon: <MdCallMissed /> };
        }
        if (isFailed) {
            return { text: 'Failed', color: '#ff9800', icon: <MdCallEnd /> };
        }
        if (isCompleted) {
            return { text: call.duration_formatted || 'Completed', color: '#4caf50', icon: isInbound ? <MdCallReceived /> : <MdCallMade /> };
        }
        return { text: call.status, color: '#999', icon: <MdCall /> };
    };

    const statusInfo = getStatusInfo();

    return (
        <div
            className="call-list-item"
            style={{
                borderBottom: `1px solid ${theme.divider}`,
                backgroundColor: isMissed ? theme.missedBg : theme.bg,
            }}
        >
            <div className="call-item-icon" style={{ color: statusInfo.color }}>
                {statusInfo.icon}
            </div>

            <div className="call-item-info">
                <div className="call-item-phone" style={{ color: theme.text }}>
                    {formatPhone(displayPhone)}
                </div>
                <div className="call-item-details" style={{ color: theme.secondaryText }}>
                    <span className={`call-direction ${isInbound ? 'inbound' : 'outbound'}`}>
                        {isInbound ? '↙ Incoming' : '↗ Outgoing'}
                    </span>
                    <span className="call-separator">•</span>
                    <span style={{ color: statusInfo.color }}>{statusInfo.text}</span>
                    {hasRecording && (
                        <>
                            <span className="call-separator">•</span>
                            <span style={{ color: '#9c27b0', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                <MdHeadset size={12} /> Recording
                            </span>
                        </>
                    )}
                </div>
            </div>

            <div className="call-item-time" style={{ color: theme.secondaryText }}>
                {formatDateTime(call.started_at || call.answered_at || call.ended_at)}
            </div>

            <div className="call-item-actions">
                {hasRecording && (
                    <Tooltip title={isCurrentlyPlaying ? "Pause Recording" : "Play Recording"}>
                        <IconButton
                            onClick={() => onPlayRecording(call.call_id, call.recording_url)}
                            size="small"
                            style={{
                                color: isCurrentlyPlaying ? '#4caf50' : '#9c27b0',
                                backgroundColor: isCurrentlyPlaying ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
                            }}
                        >
                            {isCurrentlyPlaying ? <MdPause /> : <MdPlayArrow />}
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Call Back">
                    <IconButton
                        onClick={() => onCallBack(displayPhone)}
                        size="small"
                        style={{ color: theme.accent }}
                    >
                        <MdCall />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    );
};

// Dialer component
const Dialer = ({ theme, onDial, isDialing }) => {
    const [phoneNumber, setPhoneNumber] = useState('');

    const handleDial = () => {
        if (phoneNumber.trim()) {
            onDial(phoneNumber);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleDial();
        }
    };

    return (
        <div className="dialer-container" style={{ backgroundColor: theme.cardBg }}>
            <div className="dialer-title" style={{ color: theme.text }}>
                <MdDialpad /> Quick Dial
            </div>
            <div className="dialer-input-row">
                <TextField
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter phone number (e.g., +91 98765 43210)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={isDialing}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.inputBg,
                            color: theme.text,
                            '& fieldset': { borderColor: theme.border },
                            '&:hover fieldset': { borderColor: theme.accent },
                        },
                        '& .MuiInputBase-input': { color: theme.text },
                        '& .MuiInputBase-input::placeholder': { color: theme.secondaryText },
                    }}
                />
                <button
                    className="dial-button"
                    onClick={handleDial}
                    disabled={!phoneNumber.trim() || isDialing}
                    style={{
                        backgroundColor: theme.accent,
                        opacity: (!phoneNumber.trim() || isDialing) ? 0.5 : 1,
                    }}
                >
                    {isDialing ? <CircularProgress size={20} color="inherit" /> : <MdCall size={24} />}
                </button>
            </div>
        </div>
    );
};

// Main CallManager component
const CallManager = ({ open, onClose, isDarkMode = false }) => {
    const { initiateCall, showPermissionDialog } = useContext(CallContext);

    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total_pages: 1 });
    const [isDialing, setIsDialing] = useState(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [directionFilter, setDirectionFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Audio player state
    const [isPlaying, setIsPlaying] = useState(false);
    const [playingCallId, setPlayingCallId] = useState(null);
    const audioRef = useRef(null);

    // Theme
    const theme = {
        bg: isDarkMode ? "#1a1a1a" : "#ffffff",
        cardBg: isDarkMode ? "#252525" : "#f8f9fa",
        text: isDarkMode ? "#e0e0e0" : "#333333",
        secondaryText: isDarkMode ? "#aaaaaa" : "#666666",
        hover: isDarkMode ? "#2d2d2d" : "#f5f5f5",
        border: isDarkMode ? "#333333" : "#e0e0e0",
        accent: "#25D366",
        divider: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
        inputBg: isDarkMode ? "#333" : "#fff",
        missedBg: isDarkMode ? "#2a1f1f" : "#fff5f5",
    };

    // Fetch calls
    const fetchCalls = useCallback(async (pageNum = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', pageNum);
            if (statusFilter) params.append('status', statusFilter);
            if (directionFilter) params.append('direction', directionFilter);
            if (searchQuery) params.append('search', searchQuery);

            const response = await axios.get(`${API_BASE}/calls/history/?${params.toString()}`);

            if (response.data.success) {
                setCalls(response.data.calls);
                setPagination(response.data.pagination);
            } else {
                toast.error(response.data.error || 'Failed to load calls');
            }
        } catch (error) {
            console.error('Error fetching calls:', error);
            toast.error('Failed to load call history');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, directionFilter, searchQuery]);

    // Load on open
    useEffect(() => {
        if (open) {
            setPage(1);
            fetchCalls(1);
        }
    }, [open, fetchCalls]);

    // Handle dial - uses the CallContext initiateCall function (same as CallButton)
    // This ensures WebRTC/SDP is properly handled
    const handleDial = async (phoneNumber) => {
        setIsDialing(true);
        try {
            // Clean phone number - remove spaces, dashes, and leading +
            const cleanPhone = phoneNumber.replace(/[\s\-\+]/g, '');

            // Use default business number (same as CallButton)
            const businessNumber = '918278244128';

            // Use CallContext's initiateCall which handles WebRTC/SDP
            const result = await initiateCall(cleanPhone, businessNumber, {
                agent_id: 'web-agent',
                agent_name: 'Call Manager'
            });

            if (result.success) {
                toast.success(`Calling ${phoneNumber}...`);
                onClose(); // Close modal during call
            } else if (result.error_code === 'PERMISSION_REQUIRED') {
                // Show global permission dialog
                showPermissionDialog(cleanPhone, businessNumber);
            } else {
                toast.error(result.error || 'Failed to dial');
            }
        } catch (error) {
            console.error('Dial error:', error);
            toast.error(error.message || 'Failed to dial');
        } finally {
            setIsDialing(false);
        }
    };

    // Handle callback
    const handleCallBack = (phoneNumber) => {
        handleDial(phoneNumber);
    };

    // Handle play/pause recording
    const handlePlayRecording = (callId, recordingUrl) => {
        // If clicking same call that's playing, toggle pause/play
        if (playingCallId === callId && audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
                setIsPlaying(false);
            } else {
                audioRef.current.play();
                setIsPlaying(true);
            }
            return;
        }

        // Stop current playback if any
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        // Start new playback
        const audio = new Audio(recordingUrl);

        audio.onplay = () => {
            setIsPlaying(true);
            setPlayingCallId(callId);
        };

        audio.onpause = () => {
            setIsPlaying(false);
        };

        audio.onended = () => {
            setIsPlaying(false);
            setPlayingCallId(null);
        };

        audio.onerror = () => {
            toast.error('Failed to play recording');
            setIsPlaying(false);
            setPlayingCallId(null);
        };

        audioRef.current = audio;
        audio.play().catch(e => {
            console.error('Playback error:', e);
            toast.error('Failed to play recording');
        });
    };

    // Pagination
    const handleNextPage = () => {
        if (pagination.has_next) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchCalls(nextPage);
        }
    };

    const handlePrevPage = () => {
        if (pagination.has_prev) {
            const prevPage = page - 1;
            setPage(prevPage);
            fetchCalls(prevPage);
        }
    };

    // Apply filters
    const applyFilters = () => {
        setPage(1);
        fetchCalls(1);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            styles={{
                modal: {
                    maxWidth: "800px",
                    width: "95%",
                    padding: "0",
                    borderRadius: "16px",
                    backgroundColor: theme.cardBg,
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                },
                overlay: {
                    background: "rgba(0, 0, 0, 0.7)",
                    backdropFilter: "blur(4px)",
                },
            }}
            center
            showCloseIcon={false}
        >
            {/* Header */}
            <div className="call-manager-header" style={{ backgroundColor: theme.bg, borderBottom: `1px solid ${theme.divider}` }}>
                <div className="header-left">
                    <MdPhone size={24} style={{ color: theme.accent }} />
                    <h2 style={{ color: theme.text }}>Call Manager</h2>
                    {loading && <CircularProgress size={20} style={{ color: theme.accent }} />}
                </div>

                <div className="header-actions">
                    {/* Pagination */}
                    <Tooltip title="Previous Page">
                        <span>
                            <IconButton
                                onClick={handlePrevPage}
                                disabled={!pagination.has_prev}
                                style={{ color: theme.text, opacity: !pagination.has_prev ? 0.3 : 1 }}
                            >
                                <IoMdArrowRoundBack />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <span style={{ fontSize: "14px", color: theme.text, fontWeight: "600" }}>
                        {page} / {pagination.total_pages || 1}
                    </span>
                    <Tooltip title="Next Page">
                        <span>
                            <IconButton
                                onClick={handleNextPage}
                                disabled={!pagination.has_next}
                                style={{ color: theme.text, opacity: !pagination.has_next ? 0.3 : 1 }}
                            >
                                <IoMdArrowRoundForward />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <div className="header-divider" style={{ backgroundColor: theme.divider }}></div>

                    <Tooltip title="Refresh">
                        <IconButton onClick={() => fetchCalls(page)} style={{ color: theme.text }}>
                            <IoIosRefresh className={loading ? "spin-animation" : ""} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Close">
                        <IconButton onClick={onClose} style={{ color: theme.text }}>
                            <IoMdClose />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            {/* Dialer */}
            <Dialer theme={theme} onDial={handleDial} isDialing={isDialing} />

            {/* Filters */}
            <div className="call-filters" style={{ backgroundColor: theme.bg, borderBottom: `1px solid ${theme.divider}` }}>
                <div className="filter-row">
                    {/* Search */}
                    <div className="filter-search">
                        <MdSearch style={{ color: theme.secondaryText }} />
                        <input
                            type="text"
                            placeholder="Search phone number..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                            style={{
                                backgroundColor: 'transparent',
                                color: theme.text,
                                border: 'none',
                            }}
                        />
                    </div>

                    {/* Status filter */}
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); }}
                        className="filter-select"
                        style={{ backgroundColor: theme.inputBg, color: theme.text, borderColor: theme.border }}
                    >
                        <option value="">All Status</option>
                        <option value="completed">Completed</option>
                        <option value="missed">Missed</option>
                        <option value="failed">Failed</option>
                    </select>

                    {/* Direction filter */}
                    <select
                        value={directionFilter}
                        onChange={(e) => { setDirectionFilter(e.target.value); }}
                        className="filter-select"
                        style={{ backgroundColor: theme.inputBg, color: theme.text, borderColor: theme.border }}
                    >
                        <option value="">All Direction</option>
                        <option value="inbound">Incoming</option>
                        <option value="outbound">Outgoing</option>
                    </select>

                    <button
                        className="filter-apply-btn"
                        onClick={applyFilters}
                        style={{ backgroundColor: theme.accent }}
                    >
                        <MdFilterList /> Apply
                    </button>
                </div>
            </div>

            {/* Call List */}
            <div className="call-list" style={{ backgroundColor: theme.bg }}>
                {loading && calls.length === 0 ? (
                    <div className="call-list-loading">
                        <CircularProgress style={{ color: theme.accent }} />
                    </div>
                ) : calls.length === 0 ? (
                    <div className="call-list-empty" style={{ color: theme.secondaryText }}>
                        <MdHistory size={64} style={{ opacity: 0.3 }} />
                        <p>No calls found</p>
                    </div>
                ) : (
                    calls.map((call) => (
                        <CallListItem
                            key={call.id || call.call_id}
                            call={call}
                            theme={theme}
                            onCallBack={handleCallBack}
                            onPlayRecording={handlePlayRecording}
                            isPlaying={isPlaying}
                            playingCallId={playingCallId}
                        />
                    ))
                )}
            </div>

            {/* Inline styles */}
            <style>{`
                .spin-animation {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </Modal>
    );
};

export default CallManager;
